import React, { ReactNode, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import colors from "../../design/colors";
import fonts from "../../design/typography";
import uiTokens from "../../design/uiTokens";
import { modalLayout } from "../../design/modalLayout";
import IconX from "../icons/x";

/**
 * 모든 새 모달의 공통 틀 (2026-10-02).
 * 배경·상자·제목·닫기·본문 스크롤·버튼 줄·Escape/바깥 클릭을 여기서만 처리한다.
 * - 상자는 화면 높이를 넘지 않는다. 넘치면 본문만 스크롤되고 제목과 버튼 줄은 항상 보인다.
 * - body 에 붙여 그리므로 부모 모달 안에서 열어도 뒤로 깔리지 않는다. 나중에 연 모달이 위에 온다.
 * - Escape 는 맨 위 모달 하나만 닫는다. 바깥 클릭은 누르고 뗀 곳이 모두 배경일 때만 닫는다(본문에서 드래그하다 놓아도 안 닫힘).
 */
interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  /** 제목. 머리글에 고정되어 본문을 스크롤해도 움직이지 않는다 */
  title?: ReactNode;
  /** left: 제목과 X 가 한 줄. center: X 줄 아래에 가운데 제목 (유형 선택처럼 제목이 주인공인 모달) */
  titleAlign?: "left" | "center";
  /** 제목이 없을 때 화면 읽기용 이름 */
  ariaLabel?: string;
  /** 버튼 줄. 본문 스크롤 밖에 놓인다 */
  footer?: ReactNode;
  children: ReactNode;
  /** 상자 너비. 화면보다 넓으면 화면에 맞춰 줄어든다 */
  width?: string;
  closeOnBackdrop?: boolean;
  closeOnEsc?: boolean;
  /** 닫기 X. 확인창처럼 버튼으로만 답하는 모달은 끈다(Escape·바깥 클릭은 위 옵션을 따른다) */
  showClose?: boolean;
  /**
   * body 로 옮기지 않고 그 자리에 그린다. 기존 공통 확인창(common/ConfirmModal)을 감쌀 때만 쓴다:
   * 예전 부모 모달들이 "내 상자 안 클릭인가"를 DOM 포함 관계로 판단해서, 자리를 옮기면 확인창 클릭에 부모가 닫힌다.
   */
  inline?: boolean;
}

// 열고 닫는 움직임은 common/ConfirmModal 과 같다
const ANIMATION_MS = 300;
// 열린 순서 번호. Escape 는 번호가 가장 큰(맨 위) 모달만 처리한다.
// 부모·자식이 동시에 열리면 effect 는 자식이 먼저 돌기 때문에 등록 순서가 아니라 렌더 순서로 번호를 매긴다
let openSeq = 0;
const openModals = new Set<number>();

const ModalShell = ({
  isOpen,
  onClose,
  title,
  titleAlign = "left",
  ariaLabel,
  footer,
  children,
  width = "480px",
  closeOnBackdrop = true,
  closeOnEsc = true,
  showClose = true,
  inline = false,
}: ModalShellProps) => {
  const [mounted, setMounted] = useState(isOpen);
  const [shown, setShown] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const pressedOnBackdrop = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useId();
  const seqRef = useRef(0);
  if (isOpen && seqRef.current === 0) seqRef.current = ++openSeq;
  if (!isOpen) seqRef.current = 0;

  useEffect(() => {
    let frame = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (isOpen) {
      setMounted(true);
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => setShown(true));
      });
    } else {
      setShown(false);
      timer = setTimeout(() => setMounted(false), ANIMATION_MS);
    }
    return () => {
      cancelAnimationFrame(frame);
      if (timer) clearTimeout(timer);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const seq = seqRef.current;
    openModals.add(seq);
    const isTop = () => seq === Math.max(...Array.from(openModals));
    // 바깥(부모) 모달이 Escape 를 keydown 또는 keyup 으로 받아 같이 닫히지 않도록 맨 위 모달이 두 이벤트를 모두 먼저 가져간다
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !isTop()) return;
      e.stopPropagation();
      if (closeOnEsc) onCloseRef.current();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isTop()) e.stopPropagation();
    };
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
      openModals.delete(seq);
    };
  }, [isOpen, closeOnEsc]);

  // 열 때 상자로 포커스를 옮기고, 닫으면 연 버튼으로 돌려준다
  useEffect(() => {
    if (!mounted) return;
    const previous = document.activeElement as HTMLElement | null;
    const box = boxRef.current;
    if (box && !box.contains(document.activeElement)) box.focus();
    return () => previous?.focus?.();
  }, [mounted]);

  if (!mounted) return null;

  const trapTab = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    // 부모(reactstrap 등)가 React 트리로 올라온 Tab 을 받아 포커스를 자기 쪽으로 가져가지 않게 한다
    e.stopPropagation();
    const controls = Array.from(
      e.currentTarget.querySelectorAll<HTMLElement>(
        'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (controls.length === 0) {
      e.preventDefault();
      return;
    }
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === e.currentTarget)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const shell = (
    <Backdrop
      $shown={shown}
      data-modal-shell="true"
      // 예전 모달들의 바깥 클릭 처리는 이 표식이 있는 곳의 클릭을 "위에 뜬 확인창 클릭"으로 보고 무시한다
      data-confirm-modal="true"
      onMouseDown={(e) => {
        pressedOnBackdrop.current = e.target === e.currentTarget;
        // 배경 클릭은 이 모달의 일이다. 아래 모달의 바깥 클릭 처리(document 리스너·React 부모)로 넘기지 않는다
        if (e.target === e.currentTarget) e.stopPropagation();
      }}
      onClick={(e) => {
        const fromBackdrop = pressedOnBackdrop.current && e.target === e.currentTarget;
        pressedOnBackdrop.current = false;
        if (e.target === e.currentTarget) e.stopPropagation();
        if (fromBackdrop && closeOnBackdrop) onClose();
      }}
    >
      <Box
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={title ? undefined : ariaLabel}
        tabIndex={-1}
        $shown={shown}
        $width={width}
        onKeyDown={trapTab}
      >
        {(title || showClose) && (
          <Header $center={titleAlign === "center"}>
            {title && (
              <Title id={titleId} $center={titleAlign === "center"}>
                {title}
              </Title>
            )}
            {showClose && (
              <CloseButton type="button" aria-label="닫기" onClick={onClose}>
                <IconX width="24px" height="24px" color={colors.gray900} />
              </CloseButton>
            )}
          </Header>
        )}
        <Body>{children}</Body>
        {footer && <Footer>{footer}</Footer>}
      </Box>
    </Backdrop>
  );

  return inline ? shell : createPortal(shell, document.body);
};

export default ModalShell;

const Backdrop = styled.div<{ $shown: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${modalLayout.zIndex};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: calc(${modalLayout.viewportGap} / 2) 16px;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0.3);
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  transition: opacity ${ANIMATION_MS}ms ease;
`;

const Box = styled.div<{ $shown: boolean; $width: string }>`
  display: flex;
  flex-direction: column;
  width: ${({ $width }) => $width};
  max-width: 100%;
  max-height: 100%;
  box-sizing: border-box;
  background-color: ${colors.white};
  border: 1px solid ${colors.gray100};
  border-radius: ${uiTokens.radius.dialog};
  box-shadow: 0 16px 48px rgba(27, 29, 31, 0.16);
  outline: none;
  /* 열린 뒤에는 transform 을 없앤다. 남아 있으면 안에서 여는 기존 fixed 모달이 화면이 아니라 이 상자 기준으로 갇힌다 */
  transform: ${({ $shown }) => ($shown ? "none" : "translateY(-50px)")};
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  transition: transform ${ANIMATION_MS}ms ease, opacity ${ANIMATION_MS}ms ease;
`;

// 머리글(제목·X)과 버튼 줄은 스크롤 밖에 있다. 본문만 스크롤된다
const Header = styled.div<{ $center: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 24px 24px 0;
  flex-shrink: 0;
  /* 제목이 없으면 X 만 오른쪽에 */
  & > button:only-child {
    margin-left: auto;
  }
  /* 가운데 제목: X 가 위 줄 오른쪽, 제목이 아래 줄 가운데 */
  ${({ $center }) =>
    $center &&
    `
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 24px;
    & > button { align-self: flex-end; }
  `}
`;

const Title = styled.h2<{ $center: boolean }>`
  margin: 0;
  min-width: 0;
  ${fonts.title3SemiBold as any};
  color: ${colors.gray900};
  text-align: ${({ $center }) => ($center ? "center" : "left")};
  overflow-wrap: anywhere;
`;

const CloseButton = styled.button`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 24px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;
  padding: 0 24px 24px;
`;
